import React, { useMemo, useState, useRef, useEffect, useLayoutEffect } from 'react';
import ReactDOM from 'react-dom';
import { ChevronDown, Search, X, Calendar, Filter, RotateCcw, Check } from 'lucide-react';

function useOutsideClick(refs, onClose) {
  useEffect(() => {
    function handler(e) {
      const inside = refs.some((r) => r.current && r.current.contains(e.target));
      if (!inside) onClose();
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [refs, onClose]);
}

/* Portal popover — positions itself relative to a trigger button and
   renders into document.body with fixed positioning so nothing clips it. */
function Popover({ anchorRef, open, onClose, children, minWidth = 240 }) {
  const popRef = useRef(null);
  const [style, setStyle] = useState({ top: 0, left: 0, minWidth });

  useLayoutEffect(() => {
    if (!open || !anchorRef.current) return;
    const rect = anchorRef.current.getBoundingClientRect();
    const w = Math.max(rect.width, minWidth);
    let left = rect.left;
    if (left + w > window.innerWidth - 8) left = window.innerWidth - w - 8;
    setStyle({ top: rect.bottom + 6, left, minWidth: w });
  }, [open, anchorRef, minWidth]);

  useOutsideClick([anchorRef, popRef], () => open && onClose());

  if (!open) return null;
  return ReactDOM.createPortal(
    <div ref={popRef} dir="rtl"
      className="bg-[#0B1424] border border-white/15 rounded-lg shadow-2xl overflow-hidden"
      style={{ position: 'fixed', zIndex: 10000, ...style }}>
      {children}
    </div>,
    document.body
  );
}

function MultiSelect({ label, testId, values = [], options = [], onChange, placeholder }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const anchorRef = useRef(null);

  const filtered = useMemo(() => {
    if (!q) return options;
    const nq = q.toLowerCase();
    return options.filter((o) => String(o).toLowerCase().includes(nq));
  }, [q, options]);

  const summary = values.length === 0 ? 'الكل' : values.length === 1 ? String(values[0]) : `${values.length} محدد`;
  const toggle = (v) => {
    if (values.includes(v)) onChange(values.filter((x) => x !== v));
    else onChange([...values, v]);
  };

  return (
    <div className="relative min-w-0">
      <div className="text-slate-400 text-[10.5px] font-medium mb-1 truncate">{label}</div>
      <button ref={anchorRef} onClick={() => setOpen(!open)}
        data-testid={testId ? `filter-${testId}-trigger` : undefined}
        className={`w-full bg-[#0B1424] border ${open ? 'border-blue-500/60' : 'border-white/10'} hover:border-white/25 rounded-lg px-2.5 py-2 flex items-center gap-2 text-right transition-colors`}>
        <ChevronDown size={14} className={`text-slate-400 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
        <span className={`text-[12.5px] flex-1 truncate ${values.length === 0 ? 'text-slate-400' : 'text-slate-100 font-semibold'}`}>{summary}</span>
      </button>

      <Popover anchorRef={anchorRef} open={open} onClose={() => setOpen(false)}>
        <div className="p-2 border-b border-white/5">
          <div className="bg-[#131F35] border border-white/10 rounded-md px-2 py-1.5 flex items-center gap-2">
            <Search size={12} className="text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={placeholder || 'بحث...'}
              className="bg-transparent outline-none text-slate-200 text-[12px] flex-1 min-w-0 text-right placeholder:text-slate-500" />
          </div>
        </div>
        <div className="max-h-60 overflow-y-auto py-1" data-testid={testId ? `filter-${testId}-options` : undefined}>
          <button onClick={(e) => { e.stopPropagation(); onChange([]); }} className="w-full flex items-center justify-between px-3 py-1.5 text-[12px] text-slate-300 hover:bg-white/5">
            <span className="text-blue-300">اختيار الكل</span>
            {values.length === 0 && <Check size={13} className="text-blue-400" />}
          </button>
          {filtered.length === 0 && <div className="px-3 py-2 text-slate-500 text-[11.5px]">لا توجد نتائج</div>}
          {filtered.map((o) => {
            const active = values.includes(o);
            return (
              <button key={o} onClick={(e) => { e.stopPropagation(); toggle(o); }}
                data-testid={testId ? `filter-${testId}-option-${o}` : undefined}
                className={`w-full flex items-center justify-between px-3 py-1.5 text-[12px] text-slate-200 hover:bg-white/5 ${active ? 'bg-blue-500/10' : ''}`} title={o}>
                <span className={`w-3.5 h-3.5 rounded border ${active ? 'border-blue-500 bg-blue-500' : 'border-white/20'} flex items-center justify-center shrink-0`}>
                  {active && <Check size={10} className="text-white" />}
                </span>
                <span className="truncate text-right flex-1 mr-2">{o}</span>
              </button>
            );
          })}
        </div>
        {values.length > 0 && (
          <div className="border-t border-white/5 p-2 flex items-center justify-between">
            <button onClick={(e) => { e.stopPropagation(); onChange([]); }} className="text-slate-400 hover:text-red-400 text-[11.5px] flex items-center gap-1"><X size={11} /> مسح</button>
            <span className="text-blue-300 text-[11.5px]">محدد: {values.length}</span>
          </div>
        )}
      </Popover>
    </div>
  );
}

function SchoolPicker({ value, schools = [], onChange }) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);

  const selected = useMemo(() => schools.find((s) => s.id === value), [schools, value]);
  const list = useMemo(() => {
    if (!q) return schools.slice(0, 200);
    const nq = q.toLowerCase();
    return schools.filter((s) => (s.name || '').toLowerCase().includes(nq) || (s.id || '').includes(nq)).slice(0, 200);
  }, [q, schools]);

  return (
    <div className="relative min-w-0">
      <div className="text-slate-400 text-[10.5px] font-medium mb-1 truncate">المدرسة</div>
      <div ref={anchorRef} onClick={() => setOpen(true)}
        className={`w-full bg-[#0B1424] border ${open ? 'border-blue-500/60' : 'border-white/10'} hover:border-white/25 rounded-lg px-2.5 py-2 flex items-center gap-2 transition-colors cursor-text`}>
        <Search size={13} className="text-slate-400 shrink-0" />
        <input value={open ? q : (selected ? selected.name : '')} onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder={value && value !== 'all' ? '' : 'كل المدارس'}
          className="bg-transparent outline-none text-slate-100 text-[12.5px] flex-1 min-w-0 text-right placeholder:text-slate-400 truncate" />
        {value && value !== 'all' && (
          <button onClick={(e) => { e.stopPropagation(); onChange('all'); setQ(''); }} className="text-slate-500 hover:text-red-400"><X size={12} /></button>
        )}
      </div>
      <Popover anchorRef={anchorRef} open={open && list.length > 0} onClose={() => setOpen(false)} minWidth={280}>
        <div className="max-h-64 overflow-y-auto">
          <button onClick={() => { onChange('all'); setOpen(false); setQ(''); }}
            className="w-full px-3 py-1.5 text-right text-blue-300 text-[12px] hover:bg-white/5 border-b border-white/5">— كل المدارس —</button>
          {list.map((s) => (
            <button key={s.id} onClick={() => { onChange(s.id); setOpen(false); setQ(''); }}
              className={`w-full px-3 py-1.5 text-right text-[12px] hover:bg-white/5 border-b border-white/5 truncate ${value === s.id ? 'bg-blue-500/10 text-white' : 'text-slate-200'}`}
              title={`${s.name} — ${s.governorate}`}>
              {s.name}
              <span className="text-slate-500 text-[10px] mr-2">({s.governorate})</span>
            </button>
          ))}
        </div>
      </Popover>
    </div>
  );
}

function DateRange({ from, to, onChange }) {
  return (
    <div className="min-w-0">
      <div className="text-slate-400 text-[10.5px] font-medium mb-1 truncate">النطاق الزمني</div>
      <div className="flex items-center gap-1">
        <div className="bg-[#0B1424] border border-white/10 hover:border-white/25 rounded-lg px-2 py-2 flex items-center gap-1.5 flex-1 min-w-0 transition-colors">
          <Calendar size={12} className="text-slate-400 shrink-0" />
          <input type="date" value={from || ''} onChange={(e) => onChange({ from: e.target.value || null, to })}
            className="bg-transparent outline-none text-slate-100 text-[11.5px] flex-1 min-w-0" title="من" />
        </div>
        <span className="text-slate-500 text-[10px]">→</span>
        <div className="bg-[#0B1424] border border-white/10 hover:border-white/25 rounded-lg px-2 py-2 flex items-center gap-1.5 flex-1 min-w-0 transition-colors">
          <Calendar size={12} className="text-slate-400 shrink-0" />
          <input type="date" value={to || ''} onChange={(e) => onChange({ from, to: e.target.value || null })}
            className="bg-transparent outline-none text-slate-100 text-[11.5px] flex-1 min-w-0" title="إلى" />
        </div>
      </div>
    </div>
  );
}

export default function TopFilterBar({ options = {}, value, onChange, onApply, onReset, dirty, loading, activeCount }) {
  return (
    <div className="panel p-3 mb-3" style={{ overflow: 'visible' }}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Filter size={14} className="text-blue-400" />
          <span className="text-slate-200 text-[13px] font-bold">الفلترة الذكية</span>
          {activeCount > 0 && <span className="text-[10.5px] bg-blue-500/20 text-blue-200 rounded-full px-2 py-0.5 font-semibold">{activeCount} فلتر نشط</span>}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={onReset} data-testid="filter-reset-btn" className="px-3 py-1.5 rounded-lg border border-white/10 text-slate-300 text-[12px] flex items-center gap-1 hover:bg-white/5 transition-colors">
            <RotateCcw size={13} /> مسح
          </button>
          <button onClick={onApply} disabled={loading}
            data-testid="filter-apply-btn"
            className={`px-4 py-1.5 rounded-lg text-white text-[12.5px] font-semibold flex items-center gap-1.5 transition-all ${dirty ? 'bg-blue-600 hover:bg-blue-500 shadow-md shadow-blue-500/20' : 'bg-blue-600/60 hover:bg-blue-600/80'} disabled:opacity-60`}>
            <Check size={13} /> تطبيق
            {dirty && <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"></span>}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-10 gap-2">
        <MultiSelect testId="status" label="الحالة" values={value.status} options={options.statuses || []} onChange={(v) => onChange({ ...value, status: v })} />
        <MultiSelect testId="priority" label="الأولوية" values={value.priority} options={options.priorities || []} onChange={(v) => onChange({ ...value, priority: v })} />
        <MultiSelect testId="region" label="المنطقة" values={value.region} options={options.regions || []} onChange={(v) => onChange({ ...value, region: v })} />
        <MultiSelect testId="governorate" label="المحافظة" values={value.governorate} options={options.governorates || []} onChange={(v) => onChange({ ...value, governorate: v })} />
        <MultiSelect testId="education-office" label="إدارة التعليم" values={value.education_office} options={options.education_offices || []} onChange={(v) => onChange({ ...value, education_office: v })} />
        <div className="col-span-2"><SchoolPicker value={value.school_id} schools={options.schools || []} onChange={(v) => onChange({ ...value, school_id: v })} /></div>
        <MultiSelect testId="contractor" label="المقاول" values={value.contractor} options={options.contractors || []} onChange={(v) => onChange({ ...value, contractor: v })} />
        <div className="col-span-3"><DateRange from={value.date_from} to={value.date_to} onChange={({ from, to }) => onChange({ ...value, date_from: from, date_to: to })} /></div>
      </div>
    </div>
  );
}
